// src/hooks/useRecursiveAxiosCaller.js
import { useState, useRef, useCallback, useEffect } from 'react';
import { baseOcrRequest } from './api';

const useRecursiveAxiosCaller = (maxAttempts = 10,pollInterval = 1000, conditionFn) => {
  const [status, setStatus] = useState('idle'); // idle, running, completed, stopped, error
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [lastResponse, setLastResponse] = useState(null);
  const [finalResult, setFinalResult] = useState(null);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  
  const abortControllerRef = useRef(null);
  const isStoppedRef = useRef(false);
  const requestParamsRef = useRef(null);
  const extraParamsRef = useRef(null);
  
  // 事件监听器
  const eventListenersRef = useRef({
    onResponse: [],
    onSuccess: [],
    onError: [],
    onComplete: [],
    onAttempt: [],
    onStatusChange: []
  });
  
  // 添加日志
  const addLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = {timestamp, message, type};
    setLogs(prev => [...prev, logEntry]);
    return logEntry;
  }, []);
  
  // 添加事件监听器
  const on = useCallback((event, callback) => {
    if (eventListenersRef.current[event]) {
      eventListenersRef.current[event].push(callback);
    }
    
    // 返回取消监听函数
    return () => {
      if (eventListenersRef.current[event]) {
        eventListenersRef.current[event] = eventListenersRef.current[event].filter(
          cb => cb !== callback
        );
      }
    };
  }, []);
  
  // 触发事件
  const emit = useCallback((event, ...args) => {
    if (eventListenersRef.current[event]) {
      eventListenersRef.current[event].forEach(callback => {
        try {
          callback(...args);
        } catch (err) {
          console.error(`Error in ${event} listener:`, err);
        }
      });
    }
  }, []);
  
  // 真实 API 调用
  const makeApiCall = useCallback(async (params) => {
    // 创建新的 AbortController
    abortControllerRef.current = new AbortController();
    
    // 使用传入的参数进行请求
    const response = await baseOcrRequest({
      ...params,
      config: {
        signal: abortControllerRef.current.signal
      }
    });
    return response;

    // const response = await mockJson;
    // return response;
    
    
  }, []);
  
  // 检查是否完成
  const isDone = useCallback((response) => {
    return conditionFn(response);
  }, [conditionFn]);
  
  // 开始调用（接受参数）
  const start = useCallback(async (params = {},extra) => {
    setStatus('running');
    setError(null);
    setFinalResult(null);
    setLogs([]);
    setCurrentAttempt(0);
    isStoppedRef.current = false;
    abortControllerRef.current = null;
    
    // 保存请求参数
    requestParamsRef.current = params;
    extraParamsRef.current = extra;
    
    
    addLog('开始递归调用', 'info');
    if (Object.keys(params).length > 0) {
      addLog(`请求参数: ${JSON.stringify(params)}`, 'info');
    }
    
    // 触发状态变化事件
    emit('onStatusChange', 'running');
    
    try {
      let result = null;
      
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (isStoppedRef.current) break;
        
        setCurrentAttempt(attempt);
        const attemptLog = addLog(`尝试 #${attempt}: 调用API...`);
        
        // 触发尝试事件
        emit('onAttempt', attempt, attemptLog);
        
        try {
          // 使用保存的参数进行请求
          const response = await makeApiCall(requestParamsRef.current);
          setLastResponse(response);
          const successLog = addLog(`尝试 #${attempt}: 成功获取数据`, 'success');
        
          // 触发响应事件
          emit('onResponse', response, attempt, successLog,extraParamsRef.current);
          
          if (isDone(response)) {
            const successLog = addLog(`尝试 #${attempt}: 满足完成条件`, 'success');
            result = response;
            
            // 触发成功事件
            emit('onSuccess', response, attempt, successLog);
            break;
          }
          
          addLog(`尝试 #${attempt}: 未满足完成条件，继续尝试`);
        } catch (err) {
          setLastResponse({ error: err.message });
          const errorLog = addLog(`尝试 #${attempt}: 错误 - ${err.message}`, 'error');
          
          // 触发响应事件（包含错误）
          emit('onResponse', { error: err.message }, attempt, errorLog,extraParamsRef.current);
        }
        
        // 等待1秒再进行下一次尝试
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
      
      if (result) {
        setFinalResult(result);
        setStatus('completed');
        const log = addLog('调用完成', 'success');
        
        // 触发完成事件
        emit('onComplete', result, 'completed', log,extraParamsRef.current);
        return result;
      } else if (isStoppedRef.current) {
        setStatus('stopped');
        const log = addLog('调用已停止', 'info');
        
        // 触发完成事件
        emit('onComplete', null, 'stopped', log,extraParamsRef.current);
      } else {
        setStatus('stopped');
        const log = addLog('超时', 'warning');
        
        // 触发完成事件
        emit('onComplete', null, 'max_attempts', log,extraParamsRef.current);
      }
    } catch (err) {
      setError(err);
      setStatus('error');
      const log = addLog(`识别失败: ${err.message}`, 'error');
      
      // 触发错误事件
      emit('onError', err, log);
      
      // 触发完成事件
      emit('onComplete', null, 'error', log,extraParamsRef.current);
    }
  }, [maxAttempts, makeApiCall, isDone, addLog, emit]);
  
  // 停止调用
  const stop = useCallback(() => {
    if (status !== 'completed' ) {
      isStoppedRef.current = true;
      setStatus('stopping');
      const log = addLog('正在停止调用...', 'info');
      
      // 触发状态变化事件
      emit('onStatusChange', 'stopping', log);
      
      // 中断当前请求
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }
  }, [status, addLog, emit]);
  
  return {
    start,
    stop,
    on,
    status,
    currentAttempt,
    maxAttempts,
    lastResponse,
    finalResult,
    error,
    logs
  };
};

export default useRecursiveAxiosCaller;